import React, { useMemo, useCallback } from 'react'
import * as Presenter from './HostAndGuestSignUpFormPresenter'

const ErrorMessage = ({ account, errorKey }) => {
  if (!(account.errors && account.errors[errorKey])) return null
  return (
    <Presenter.ErrorMessage account={account}>
      {account.errors[errorKey]}
    </Presenter.ErrorMessage>
  )
}

const HostAndGuestSignUpForm = ({
  config,
  account,
  onSubmit,
  actions,
  canSubmit,
}) => {
  const onChange = useCallback(
    e => {
      e.preventDefault()
      const [name, value] = [e.target.name, e.target.value]
      const payload = {}
      if (name === 'acceptTermOfService') {
        payload[name] = !account.acceptTermOfService
      } else {
        payload[name] = value
      }
      actions.setAccount(payload)
    },
    [account, actions],
  )

  // submitボタンのdisabled判定のために、全ての情報が入力されているかをチェックする
  const enteredAllInformation = useMemo(
    () =>
      account.familyName &&
      account.givenName &&
      account.email &&
      account.tel &&
      account.password &&
      account.passwordConfirmation &&
      account.acceptTermOfService,
    [account],
  )

  // error component
  const errorFamilyNameComponent = useMemo(() => {
    return <ErrorMessage account={account} errorKey={'users.family_name'} />
  }, [account])
  const errorErrorGivenName = useMemo(() => {
    return <ErrorMessage account={account} errorKey={'users.given_name'} />
  }, [account])
  const errorErrorEmail = useMemo(() => {
    return <ErrorMessage account={account} errorKey={'users.email'} />
  }, [account])
  const errorErrorTel = useMemo(() => {
    return <ErrorMessage account={account} errorKey={'tel'} />
  }, [account])
  const errorErrorPassword = useMemo(() => {
    return <ErrorMessage account={account} errorKey={'users.password'} />
  }, [account])
  const errorPasswordConfirmation = useMemo(() => {
    return (
      <ErrorMessage
        account={account}
        errorKey={'users.password_confirmation'}
      />
    )
  }, [account])
  const errorErrorAcceptTermOfService = useMemo(() => {
    return (
      <ErrorMessage account={account} errorKey={'accept_term_of_service'} />
    )
  }, [account])

  const nameInputComponent = useMemo(() => {
    return (
      <Presenter.NameInput
        account={account}
        onChange={onChange}
        errorFamilyNameComponent={errorFamilyNameComponent}
        errorErrorGivenName={errorErrorGivenName}
      />
    )
  }, [account, onChange, errorFamilyNameComponent, errorErrorGivenName])
  const emailInputComponent = useMemo(() => {
    return (
      <Presenter.EmailInput
        account={account}
        onChange={onChange}
        errorErrorEmail={errorErrorEmail}
      />
    )
  }, [account, onChange, errorErrorEmail])
  const telInputComponent = useMemo(() => {
    return (
      <Presenter.TelInput
        account={account}
        onChange={onChange}
        errorErrorTel={errorErrorTel}
      />
    )
  }, [account, onChange, errorErrorTel])
  const passwordInputComponent = useMemo(() => {
    return (
      <Presenter.PasswordInput
        account={account}
        onChange={onChange}
        errorErrorPassword={errorErrorPassword}
      />
    )
  }, [account, onChange, errorErrorPassword])
  const passwordConfirmationInputComponent = useMemo(() => {
    return (
      <Presenter.PasswordConfirmationInput
        account={account}
        onChange={onChange}
        errorPasswordConfirmation={errorPasswordConfirmation}
      />
    )
  }, [account, onChange, errorPasswordConfirmation])
  const submitButtonsComponent = useMemo(() => {
    return (
      <Presenter.SubmitButtons
        onSubmit={onSubmit}
        enteredAllInformation={enteredAllInformation}
        canSubmit={canSubmit && !!account.acceptTermOfService}
      />
    )
  }, [onSubmit, enteredAllInformation])
  const termsComponent = useMemo(() => {
    return <Presenter.Terms />
  }, [])
  const acceptTermComponent = useMemo(() => {
    return (
      <Presenter.AcceptTerm
        account={account}
        onChange={onChange}
        errorErrorAcceptTermOfService={errorErrorAcceptTermOfService}
      />
    )
  }, [
    account,
    onChange,
    errorErrorAcceptTermOfService,
  ])
  const otherActionsComponent = useMemo(() => {
    return <Presenter.OtherActions config={config} />
  }, [config])

  return (
    <Presenter.HostAndGuestSignUpForm
      nameInputComponent={nameInputComponent}
      emailInputComponent={emailInputComponent}
      telInputComponent={telInputComponent}
      passwordInputComponent={passwordInputComponent}
      passwordConfirmationInputComponent={passwordConfirmationInputComponent}
      termsComponent={termsComponent}
      acceptTermComponent={acceptTermComponent}
      submitButtonsComponent={submitButtonsComponent}
      otherActionsComponent={otherActionsComponent}
      enteredAllInformation={enteredAllInformation}
    />
  )
}

export default HostAndGuestSignUpForm
